// src/pages/action/RoleActionManagement.js
// Gestión de acciones por rol (action.php) – CRUD y configuración del menú

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Table, Modal, Badge } from 'react-bootstrap';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import {
  getActions,
  getActionsByRoleHierarchical,
  createAction,
  updateAction,
  deleteAction
} from '../../services/actionApi';
import { getRoles } from '../../services/roleApi';
import AlertModal from '../../components/AlertModal';
import useAlertModal from '../../hooks/useAlertModal';

const RoleActionManagement = () => {
  const [actions, setActions] = useState([]);
  const [hierarchical, setHierarchical] = useState([]);
  const [roles, setRoles] = useState([]);
  const [roleFilter, setRoleFilter] = useState('');
  const [viewMode, setViewMode] = useState('flat');
  const [loading, setLoading] = useState(false);
  const { showAlert, showAlertModal, alertMessage, alertType, hideAlert, getErrorMessage } = useAlertModal();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCopyOneModal, setShowCopyOneModal] = useState(false);
  const [actionToCopy, setActionToCopy] = useState(null);
  const [copyOneTargetRoleId, setCopyOneTargetRoleId] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);

  const [formData, setFormData] = useState({
    role_id: '',
    action_name: '',
    url: '',
    parent_action_id: '',
    icon: '',
    order_index: ''
  });

  const loadActions = useCallback(async () => {
    try {
      setLoading(true);
      const { ok, status, data } = await getActions(roleFilter || undefined);
      if (ok && (status === 200 || data?.code === 200)) {
        const list = data.data || data.message || [];
        setActions(Array.isArray(list) ? list : []);
      } else {
        showAlert(getErrorMessage({ data, status }) || 'Error cargando acciones', 'error');
      }
    } catch (error) {
      console.error('Error cargando acciones:', error);
      showAlert('Error inesperado al cargar acciones', 'error');
    } finally {
      setLoading(false);
    }
  }, [roleFilter, showAlert, getErrorMessage]);

  const loadHierarchical = useCallback(async () => {
    if (!roleFilter) return;
    try {
      const { ok, status, data } = await getActionsByRoleHierarchical(roleFilter);
      if (ok && (status === 200 || data?.code === 200)) {
        const list = data.data || data.message || [];
        setHierarchical(Array.isArray(list) ? list : []);
      } else {
        setHierarchical([]);
      }
    } catch {
      setHierarchical([]);
    }
  }, [roleFilter]);

  const loadRoles = useCallback(async () => {
    const { ok, data } = await getRoles();
    if (ok && (data?.code === 200 || data?.data)) {
      const list = data.data || data.message || [];
      const arr = Array.isArray(list) ? list : [];
      setRoles(
        arr
          .map((r) => ({ role_id: r.id_role, role_name: r.role_name || `Rol ${r.id_role}` }))
          .filter((r) => r.role_id != null)
          .sort((a, b) => a.role_id - b.role_id)
      );
    } else {
      setRoles([]);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    loadActions();
  }, [loadActions]);

  useEffect(() => {
    if (viewMode === 'tree' && roleFilter) loadHierarchical();
    else setHierarchical([]);
  }, [viewMode, roleFilter, loadHierarchical]);

  const handleOpenCreate = () => {
    setFormData({
      role_id: roleFilter || '',
      action_name: '',
      url: '',
      parent_action_id: '',
      icon: '',
      order_index: ''
    });
    setSelectedAction(null);
    setShowCreateModal(true);
  };

  const handleOpenEdit = (action) => {
    setSelectedAction(action);
    setFormData({
      role_id: String(action.role_id ?? ''),
      action_name: action.action_name || '',
      url: action.url || '',
      parent_action_id: action.parent_action_id != null ? String(action.parent_action_id) : '',
      icon: action.icon || '',
      order_index: action.order_index != null ? String(action.order_index) : ''
    });
    setShowEditModal(true);
  };

  const handleOpenDelete = (action) => {
    setSelectedAction(action);
    setShowDeleteModal(true);
  };

  const handleCloseModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setShowCopyOneModal(false);
    setActionToCopy(null);
    setCopyOneTargetRoleId('');
    setSelectedAction(null);
  };

  const handleOpenCopyOne = (action) => {
    setActionToCopy(action);
    setCopyOneTargetRoleId('');
    setShowCopyOneModal(true);
  };

  /** Copia solo esta acción al rol elegido (se crea como raíz en el destino). */
  const handleCopyOneToRole = async (e) => {
    e.preventDefault();
    if (!actionToCopy || !copyOneTargetRoleId) {
      showAlert('Elige el rol al que copiar', 'error');
      return;
    }
    const targetRoleId = parseInt(copyOneTargetRoleId, 10);
    if (targetRoleId === actionToCopy.role_id) {
      showAlert('Elige un rol distinto al actual de la acción', 'error');
      return;
    }
    try {
      setModalLoading(true);
      const { ok, status, data } = await createAction({
        role_id: targetRoleId,
        action_name: actionToCopy.action_name,
        url: actionToCopy.url || undefined,
        parent_action_id: null,
        icon: actionToCopy.icon || undefined,
        order_index: actionToCopy.order_index
      });
      if (ok && (status === 201 || data?.code === 201)) {
        showAlert('Acción copiada al rol correctamente', 'success');
        handleCloseModals();
        loadRoles();
        loadActions();
        if (roleFilter === copyOneTargetRoleId) {
          if (viewMode === 'tree') loadHierarchical();
        }
      } else {
        showAlert(getErrorMessage({ data, status }) || 'Error al copiar la acción', 'error');
      }
    } catch (error) {
      console.error('Error copiando acción:', error);
      showAlert('Error inesperado al copiar la acción', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.role_id || !formData.action_name?.trim()) {
      showAlert('Rol y nombre de acción son obligatorios', 'error');
      return;
    }
    try {
      setModalLoading(true);
      const payload = {
        role_id: parseInt(formData.role_id, 10),
        action_name: formData.action_name.trim(),
        url: formData.url?.trim() || undefined,
        parent_action_id: formData.parent_action_id ? parseInt(formData.parent_action_id, 10) : null,
        icon: formData.icon?.trim() || undefined,
        order_index: formData.order_index !== '' ? parseInt(formData.order_index, 10) : undefined
      };
      const { ok, status, data } = await createAction(payload);
      if (ok && (status === 201 || data?.code === 201)) {
        showAlert(data?.message || 'Acción creada', 'success');
        handleCloseModals();
        loadActions();
        if (viewMode === 'tree' && roleFilter) loadHierarchical();
      } else {
        showAlert(getErrorMessage({ data, status }) || 'Error al crear acción', 'error');
      }
    } catch (error) {
      console.error('Error creando acción:', error);
      showAlert('Error inesperado al crear acción', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedAction?.id_role_action) return;
    try {
      setModalLoading(true);
      const payload = {
        role_id: formData.role_id ? parseInt(formData.role_id, 10) : undefined,
        action_name: formData.action_name?.trim(),
        url: formData.url?.trim(),
        parent_action_id: formData.parent_action_id === '' ? null : (formData.parent_action_id ? parseInt(formData.parent_action_id, 10) : undefined),
        icon: formData.icon?.trim(),
        order_index: formData.order_index !== '' ? parseInt(formData.order_index, 10) : undefined
      };
      const { ok, status, data } = await updateAction(selectedAction.id_role_action, payload);
      if (ok && status === 200) {
        showAlert(data?.message || 'Acción actualizada', 'success');
        handleCloseModals();
        loadActions();
        if (viewMode === 'tree' && roleFilter) loadHierarchical();
      } else {
        showAlert(getErrorMessage({ data, status }) || 'Error al actualizar acción', 'error');
      }
    } catch (error) {
      console.error('Error actualizando acción:', error);
      showAlert('Error inesperado al actualizar acción', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedAction?.id_role_action) return;
    try {
      setModalLoading(true);
      const { ok, status, data } = await deleteAction(selectedAction.id_role_action);
      if (ok && status === 200) {
        showAlert(data?.message || 'Acción eliminada', 'success');
        handleCloseModals();
        loadActions();
        if (viewMode === 'tree' && roleFilter) loadHierarchical();
      } else {
        showAlert(getErrorMessage({ data, status }) || 'Error al eliminar acción', 'error');
      }
    } catch (error) {
      console.error('Error eliminando acción:', error);
      showAlert('Error inesperado al eliminar acción', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const renderTree = (items, level = 0) => {
    if (!items || !items.length) return null;
    return (
      <ul className="list-unstyled mb-0" style={{ paddingLeft: level ? 24 : 0 }}>
        {items.map((item) => (
          <li key={item.id_role_action} className="mb-2">
            <div className="d-flex align-items-center gap-2 flex-wrap border rounded p-2 bg-light">
              <span className="text-muted small">#{item.id_role_action}</span>
              <strong>{item.action_name}</strong>
              {item.url && <Badge bg="secondary">{item.url}</Badge>}
              <span className="text-muted small">orden: {item.order_index ?? '-'}</span>
              <div className="ms-auto">
                <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => handleOpenCopyOne(item)} title="Copiar a otro rol">
                  <i className="fas fa-copy"></i>
                </Button>
                <Button variant="outline-primary" size="sm" className="me-1" onClick={() => handleOpenEdit(item)}>
                  <i className="fas fa-edit"></i>
                </Button>
                <Button variant="outline-danger" size="sm" onClick={() => handleOpenDelete(item)}>
                  <i className="fas fa-trash"></i>
                </Button>
              </div>
            </div>
            {item.children && item.children.length > 0 && renderTree(item.children, level + 1)}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <NavContainer />
      <OffCanvasNav />
      <Container fluid className="mt-3">
        <Row>
          <Col>
            <Card className="shadow-sm">
              <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <h5 className="mb-0">
                  <i className="fas fa-list-check me-2"></i>
                  Acciones por rol
                </h5>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Form.Select
                    style={{ width: 'auto' }}
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                  >
                    <option value="">Todos los roles</option>
                    {roles.map((r) => (
                      <option key={r.role_id} value={r.role_id}>
                        {r.role_name} (ID {r.role_id})
                      </option>
                    ))}
                  </Form.Select>
                  <div className="btn-group btn-group-sm">
                    <Button
                      variant={viewMode === 'flat' ? 'primary' : 'outline-primary'}
                      onClick={() => setViewMode('flat')}
                    >
                      Lista
                    </Button>
                    <Button
                      variant={viewMode === 'tree' ? 'primary' : 'outline-primary'}
                      onClick={() => setViewMode('tree')}
                      disabled={!roleFilter}
                      title={!roleFilter ? 'Elige un rol para ver el árbol' : ''}
                    >
                      Árbol
                    </Button>
                  </div>
                  <Button variant="success" onClick={handleOpenCreate}>
                    <i className="fas fa-plus me-1"></i>
                    Nueva acción
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" />
                  </div>
                ) : viewMode === 'tree' ? (
                  <div>
                    {!roleFilter ? (
                      <p className="text-muted">Selecciona un rol para ver la estructura jerárquica del menú.</p>
                    ) : hierarchical.length === 0 ? (
                      <p className="text-muted">No hay acciones para este rol.</p>
                    ) : (
                      renderTree(hierarchical)
                    )}
                  </div>
                ) : (
                  <Table responsive striped bordered hover>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Rol</th>
                        <th>Nombre</th>
                        <th>URL</th>
                        <th>Padre</th>
                        <th>Icono</th>
                        <th>Orden</th>
                        <th className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {actions.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center text-muted py-4">
                            {roleFilter ? 'No hay acciones para este rol' : 'No hay acciones. Crea una o filtra por rol.'}
                          </td>
                        </tr>
                      ) : (
                        actions.map((a) => (
                          <tr key={a.id_role_action}>
                            <td>{a.id_role_action}</td>
                            <td>
                              <Badge bg="info">{a.role_name || a.role_id}</Badge>
                            </td>
                            <td>{a.action_name || '-'}</td>
                            <td><code className="small">{a.url || '-'}</code></td>
                            <td>{a.parent_action_id != null ? a.parent_action_id : '-'}</td>
                            <td>{a.icon || '-'}</td>
                            <td>{a.order_index ?? '-'}</td>
                            <td className="text-center">
                              <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => handleOpenCopyOne(a)} title="Copiar esta acción a otro rol">
                                <i className="fas fa-copy"></i>
                              </Button>
                              <Button variant="outline-primary" size="sm" className="me-1" onClick={() => handleOpenEdit(a)}>
                                <i className="fas fa-edit"></i>
                              </Button>
                              <Button variant="outline-danger" size="sm" onClick={() => handleOpenDelete(a)}>
                                <i className="fas fa-trash"></i>
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Modal show={showCreateModal} onHide={handleCloseModals}>
          <Modal.Header closeButton>
            <Modal.Title>Nueva acción</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleCreate}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Rol *</Form.Label>
                <Form.Select name="role_id" value={formData.role_id} onChange={handleChange} required>
                  <option value="">Seleccionar rol</option>
                  {roles.map((r) => (
                    <option key={r.role_id} value={r.role_id}>
                      {r.role_name} (ID {r.role_id})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Nombre de la acción *</Form.Label>
                <Form.Control name="action_name" value={formData.action_name} onChange={handleChange} placeholder="Ej: Gestión de usuarios" required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>URL</Form.Label>
                <Form.Control name="url" value={formData.url} onChange={handleChange} placeholder="/gestion-usuarios" />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>ID acción padre (submenú)</Form.Label>
                <Form.Control type="number" name="parent_action_id" value={formData.parent_action_id} onChange={handleChange} placeholder="Dejar vacío si es raíz" />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Icono</Form.Label>
                <Form.Control name="icon" value={formData.icon} onChange={handleChange} placeholder="Ej: settings, people" />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Orden</Form.Label>
                <Form.Control type="number" name="order_index" value={formData.order_index} onChange={handleChange} placeholder="0" />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModals}>Cancelar</Button>
              <Button variant="primary" type="submit" disabled={modalLoading}>
                {modalLoading ? <Spinner animation="border" size="sm" /> : 'Crear'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={showEditModal} onHide={handleCloseModals}>
          <Modal.Header closeButton>
            <Modal.Title>Editar acción</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleUpdate}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Rol</Form.Label>
                <Form.Select name="role_id" value={formData.role_id} onChange={handleChange}>
                  <option value="">Seleccionar rol</option>
                  {roles.map((r) => (
                    <option key={r.role_id} value={r.role_id}>
                      {r.role_name} (ID {r.role_id})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Nombre de la acción *</Form.Label>
                <Form.Control name="action_name" value={formData.action_name} onChange={handleChange} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>URL</Form.Label>
                <Form.Control name="url" value={formData.url} onChange={handleChange} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>ID acción padre</Form.Label>
                <Form.Control type="number" name="parent_action_id" value={formData.parent_action_id} onChange={handleChange} placeholder="Vacío = raíz" />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Icono</Form.Label>
                <Form.Control name="icon" value={formData.icon} onChange={handleChange} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Orden</Form.Label>
                <Form.Control type="number" name="order_index" value={formData.order_index} onChange={handleChange} />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModals}>Cancelar</Button>
              <Button variant="primary" type="submit" disabled={modalLoading}>
                {modalLoading ? <Spinner animation="border" size="sm" /> : 'Guardar'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={showDeleteModal} onHide={handleCloseModals}>
          <Modal.Header closeButton>
            <Modal.Title>Eliminar acción</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            ¿Eliminar la acción <strong>{selectedAction?.action_name}</strong> (URL: {selectedAction?.url || '—'})? Esta acción no se puede deshacer.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModals}>Cancelar</Button>
            <Button variant="danger" onClick={handleConfirmDelete} disabled={modalLoading}>
              {modalLoading ? <Spinner animation="border" size="sm" /> : 'Eliminar'}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showCopyOneModal} onHide={handleCloseModals} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="fas fa-copy me-2"></i>
              Copiar esta acción a otro rol
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleCopyOneToRole}>
            <Modal.Body>
              {actionToCopy && (
                <div className="mb-3 p-2 bg-light rounded">
                  <strong>{actionToCopy.action_name}</strong>
                  {actionToCopy.url && <code className="ms-2 small">{actionToCopy.url}</code>}
                </div>
              )}
              <Form.Group className="mb-3">
                <Form.Label>Copiar al rol</Form.Label>
                <Form.Select
                  value={copyOneTargetRoleId}
                  onChange={(e) => setCopyOneTargetRoleId(e.target.value)}
                  required
                >
                  <option value="">Seleccionar rol</option>
                  {roles.map((r) => (
                    <option key={r.role_id} value={r.role_id} disabled={Number(r.role_id) === Number(actionToCopy?.role_id)}>
                      {r.role_name} (ID {r.role_id})
                      {Number(r.role_id) === Number(actionToCopy?.role_id) ? ' — mismo rol' : ''}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">La acción se creará como ítem de primer nivel en el rol elegido.</Form.Text>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModals}>Cancelar</Button>
              <Button variant="success" type="submit" disabled={modalLoading || !copyOneTargetRoleId || Number(copyOneTargetRoleId) === Number(actionToCopy?.role_id)}>
                {modalLoading ? <Spinner animation="border" size="sm" className="me-2" /> : <i className="fas fa-copy me-1"></i>}
                Copiar acción
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <AlertModal show={showAlertModal} onHide={hideAlert} message={alertMessage} type={alertType} />
      </Container>
    </>
  );
};

export default RoleActionManagement;
